export type PlatformEnvironment = 'staging' | 'preview' | 'production';

const ENVIRONMENT_LABELS: Record<PlatformEnvironment, string> = {
  staging: 'Staging',
  preview: 'Preview',
  production: '',
};

/**
 * Derives the *platform* deployment environment from the host the menu is embedded in.
 * Environments are keyed off a DNS label: `staging.app.beyondtrust.io` is staging,
 * `*.preview.app.beyondtrust.io` is preview. Anything else (production, localhost) is
 * treated as production. Matching is case-insensitive and looks for the label anywhere in
 * the host, so tenant subdomains (`tenant.staging.app.beyondtrust.io`) still resolve.
 *
 * Note: matching is on exact DNS labels, not substrings — a host label that merely contains
 * `preview` (e.g. `menu-preview`) is not the platform `preview` env and resolves to
 * `production` here (no badge). `environment_test.ts` guards against a regression to that.
 */
export function getPlatformEnvironment(hostname: string = window.location.hostname): PlatformEnvironment {
  const labels = hostname.toLowerCase().split('.');
  if (labels.includes('staging')) {
    return 'staging';
  }
  if (labels.includes('preview')) {
    return 'preview';
  }
  return 'production';
}

/**
 * The display badge for the current environment: `Staging`, `Preview`, or an empty string
 * for production. Deliberately not localized — an internal-only marker.
 */
export function getEnvironmentLabel(hostname: string = window.location.hostname): string {
  return ENVIRONMENT_LABELS[getPlatformEnvironment(hostname)];
}
